import type { Properties } from 'hast';
import type { Root } from 'mdast';
import {
  ContainerDirective,
  LeafDirective,
  TextDirective,
} from 'mdast-util-directive';
import type { Plugin } from 'unified';
import { visit } from 'unist-util-visit';

type AttrMap = {
  [key: string]: string;
};

interface DirectiveDataWithHast {
  hName?: string;
  hProperties?: Properties;
  [key: string]: unknown;
}

/**
 * Remark plugin that transforms entity reference directives into custom components
 *
 * Each directive expects JSON content containing entity data
 */
export const remarkEntityReference: Plugin<[], Root> = () => (tree) => {
  const entityTypes = [
    'salesOrder',
    'booking',
    'load',
    'account',
    'purchaseOrder',
    'invoice',
    'bill',
    'inventoryItem',
    'facility',
    'category',
    'inventoryTag',
    'driver',
    'truck',
    'trailer',
    'containerType',
    'shippingCarrier',
    'freightForwarder',
    'freightLine',
    'route',
    'asset',
    'task',
  ];

  const visitDirective = (
    node: ContainerDirective | LeafDirective | TextDirective,
  ) => {
    if (!entityTypes.includes(node.name)) {
      return;
    }

    const mode = node.attributes?.mode || 'card';

    // Gather attributes from directive
    const rawAttrsUnknown = {
      ...(node.attributes || {}),
    } as Record<string, unknown>;

    const flattenedAttrs: AttrMap = {};
    Object.entries(rawAttrsUnknown).forEach(([k, v]) => {
      if (k !== 'mode' && v !== undefined && v !== null && v !== '') {
        flattenedAttrs[k] = String(v);
      }
    });

    // Ensure mode is present
    flattenedAttrs.mode = mode;

    // Transform the directive node into a custom component node
    // react-markdown will map this to a React component via the components prop

    const data = (node.data || {}) as DirectiveDataWithHast;
    data.hName = node.name;
    data.hProperties = {
      mode,
      ...flattenedAttrs,
    };
    node.data = data;

    // Clear children since we've extracted the data
    node.children = [];
  };

  visit(tree, 'containerDirective', visitDirective);
  visit(tree, 'leafDirective', visitDirective);
  visit(tree, 'textDirective', visitDirective);
};
